import React, { useEffect, useState, useRef } from "react";
import { Card, Grid, List, ListItem, ListItemText, Avatar, Divider, IconButton, Skeleton } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Users, Calendar, Landmark, FileText, AlertCircle, ZoomIn, ZoomOut } from "lucide-react";
import MDBadge from "components/MDBadge";
import MDProgress from "components/MDProgress";
import PropTypes from "prop-types";
import { imageUrl , RequestService } from "services/RequestService";
import Cookies from "js-cookie";
import axios from 'axios';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().slice(0, 10).split("-").reverse().join("-");
};

const styles = {
  iconstyle: {
    color: "gray",
  },
  projectTitle: {
    color: "gray",
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
    minHeight: '400px',
    maxHeight: '600px',
    borderRadius: '12px',
    marginBottom: '2rem',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#f5f5f5',
  },
  projectImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
    transition: 'transform 0.3s ease',
    maxHeight: '600px',
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
    padding: '20px',
    color: 'white',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  zoomControls: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    display: 'flex',
    gap: '8px',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  errorContainer: {
    width: '100%',
    height: '300px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: '12px',
    color: '#666',
  }
};

const ProjectDetails = ({ initialValuesState }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (initialValuesState.imageUrl && imgRef.current) {
      let fullImageUrl = `${initialValuesState.imageUrl}`;
    
      // Create a custom axios instance without /api/ in baseURL
      const customRequest = axios.create({
        baseURL: imageUrl,
        headers: {
          Authorization: `Bearer ${Cookies.get("Authentication")}`
        }
      });

      customRequest.get(fullImageUrl, { responseType: 'blob' })
        .then(response => {
          const imageUrl = URL.createObjectURL(response.data);
          if (imgRef.current) {
            imgRef.current.src = imageUrl;
            handleImageLoad();
          }
        })
        .catch(error => {
          console.error('Error loading image:', error);
          // handleImageError();
        });

      // Cleanup
      return () => {
        if (imgRef.current && imgRef.current.src) {
          URL.revokeObjectURL(imgRef.current.src);
        }
      };
    }
  }, [initialValuesState.imageUrl]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"; // Handle empty values
  
    const [day, month, year] = dateString.split("-");
    const monthName = new Date(`${year}-${month}-${day}`).toLocaleString("en-GB", { month: "short" });
  
    return <p style={{textAlign: 'right'}} dir="ltr">{year}-{monthName}-{day}</p>
  };

  const projectStatus = () => {
    switch (initialValuesState.projectStatus) {
      case 5:
        return <MDBadge size="lg" badgeContent={initialValuesState.projectStatusName} container />;
      case 7:
        return (
          <MDBadge
            color="warning"
            size="lg"
            badgeContent={initialValuesState.projectStatusName}
            container
          />
        );
      case 6:
        return (
          <MDBadge
            color="error"
            size="lg"
            badgeContent={initialValuesState.projectStatusName}
            container
          />
        );
      case 4:
        return (
          <MDBadge
            color="success"
            size="lg"
            badgeContent={initialValuesState.projectStatusName}
            container
          />
        );
      default:
        return (
          <MDBadge
            color="info"
            size="lg"
            badgeContent={initialValuesState.projectStatusName}
            container
          />
        );
    }
  };

  return (
    <MDBox pt={5} px={10}>
      <MDBox>
        {initialValuesState.imageUrl && (
          <Card 
            sx={{ 
              ...styles.imageContainer,
              cursor: 'pointer',
              transform: isZoomed ? 'scale(1.1)' : 'scale(1)',
              '&:hover': {
                '& .overlay': {
                  opacity: 1,
                },
                '& .zoom-controls': {
                  opacity: 1,
                }
              },
              padding: '1rem',
            }}
          >
            {imageLoading && (
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height={400}
                animation="wave"
              />
            )}
            {imageError ? (
              <div style={styles.errorContainer}>
                <MDTypography>
                  صورة المشروع غير متوفرة
                </MDTypography>
              </div>
            ) : (
              <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img 
                  ref={imgRef}
                  alt={initialValuesState.name}
                  style={{
                    ...styles.projectImage,
                    transform: isZoomed ? 'scale(1.2)' : 'scale(1)',
                    display: imageLoading ? 'none' : 'block',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                  onError={handleImageError}
                  loading="lazy"
                />
                <div className="overlay" style={styles.imageOverlay}>
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    {initialValuesState?.name}
                  </MDTypography>
                  <MDTypography variant="body2" color="white">
                    {initialValuesState.clientName}
                  </MDTypography>
                </div>
                <div className="zoom-controls" style={styles.zoomControls}>
                  <IconButton 
                    onClick={toggleZoom}
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      '&:hover': { backgroundColor: 'white' }
                    }}
                  >
                    {isZoomed ? <ZoomOut size={20} /> : <ZoomIn size={20} />}
                  </IconButton>
                </div>
              </div>
            )}
          </Card>
        )}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <MDTypography variant="h4" fontWeight="bold" mb={1}>
            {initialValuesState?.name}
          </MDTypography>

          <MDTypography variant="h4" fontWeight="bold" mb={1}>
            {projectStatus()}
            {/* <MDBadge size="lg" badgeContent={initialValuesState.projectStatusName} container /> */}
          </MDTypography>
        </div>

        <MDTypography 
  style={{ ...styles.projectTitle, whiteSpace: "pre-line" }} 
  variant="body1" 
  color="text.secondary"
>
  {initialValuesState.description}
</MDTypography>

      </MDBox>
      <MDBox pt={2} px={0}>
        <Card sx={{ overflow: "visible", p: 3 }}>
          <MDTypography variant="h6" mb={1}>
            تفاصيل المشروع
          </MDTypography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={12}>
              <MDBox mb={3}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={12}>
                    <MDTypography style={styles.projectTitle} variant="h6" mt={3}>
                      التقدم
                    </MDTypography>
                    <MDProgress label={true} value={initialValuesState.progress} />
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <MDTypography style={styles.projectTitle} variant="h6" mt={1}>
                      <Users />
                      &nbsp; العميل
                    </MDTypography>
                    <MDTypography variant="body1" mt={1}>
                      {initialValuesState.clientName
                        ? initialValuesState.clientName
                        : "لا يوجد عميل"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <MDTypography style={styles.projectTitle} variant="h6" mt={1}>
                      <FileText />
                      &nbsp; التصنيف
                    </MDTypography>
                    <MDTypography variant="body1" mt={1}>
                      {initialValuesState?.categoryName|| "لا يوجد تصنيف"}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <MDTypography style={styles.projectTitle} variant="h6" mt={1}>
                      <Calendar />
                      &nbsp; تاريخ البدء
                    </MDTypography>
                    <MDTypography variant="body1" mt={1}>
                      {formatDate(initialValuesState.startedDate)}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={6} md={6}>
                    <MDTypography style={styles.projectTitle} variant="h6">
                      <Calendar />
                      &nbsp; تاريخ الانتهاء
                    </MDTypography>
                    <MDTypography variant="body1" mt={1}>
                      {formatDate(initialValuesState.endedDate)}
                    </MDTypography>
                  </Grid>
                </Grid>
              </MDBox>

              <Divider sx={{ mb: 3 }} />

              <MDBox mb={3}>
                <MDTypography variant="body1" mb={1}>
                  الميزانية
                </MDTypography>
                <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <MDTypography style={styles.projectTitle} variant="h6" mt={1}>
                    <Landmark />
                      &nbsp; حالة الميزانية
                    </MDTypography>
                    <MDTypography variant="body1" mt={1}>
                      {initialValuesState.budgetName.toLocaleString()}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography style={styles.projectTitle} variant="h6" mt={1}>
                      <Landmark />
                      &nbsp; الميزانية الكلية
                    </MDTypography>
                    <MDTypography variant="body1" mt={1}>
                      {initialValuesState.budget.toLocaleString()}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography style={styles.projectTitle} variant="h6" mt={1}>
                      <Landmark />
                      &nbsp; النفقات الفعلية
                    </MDTypography>
                    <MDTypography variant="body1" mt={1}>
                      {initialValuesState.actualExpenditure?.toLocaleString()}
                    </MDTypography>
                  </Grid>
                </Grid>
              </MDBox>

              <Divider sx={{ mb: 3 }} />

              <MDBox mb={3}>
                <MDTypography variant="body1" mb={1}>
                  طلبات التغيير
                </MDTypography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <MDTypography style={styles.projectTitle} variant="h6" mt={1}>
                      <AlertCircle />
                      &nbsp; عدد الطلبات
                    </MDTypography>
                    <MDTypography variant="body1" mt={1}>
                      {initialValuesState.changeRequestCount}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}></Grid>
                </Grid>
              </MDBox>
            </Grid>
          </Grid>
        </Card>
      </MDBox>
    </MDBox>
  );
};

ProjectDetails.propTypes = {
  initialValuesState: PropTypes.object.isRequired,
};
export default ProjectDetails;
